export type AudioMode = 'menu' | 'game';

export interface AudioMixSettings {
  masterVolume: number;
  musicVolume: number;
  ambienceVolume: number;
  sfxVolume: number;
}

export type AudioCategory = 'music' | 'ambience' | 'sfx';

export type AudioZoneFalloff = 'smooth' | 'linear';

/** A positional looping sound the engine plays with distance attenuation. */
export interface AudioZoneVoiceSpec {
  id: string;
  /** Source file URL (decoded once, cached per URL). */
  url: string;
  /** Zone centre on the ground plane (metres in 3D, tiles in 2D). */
  x: number;
  z: number;
  /** Radius beyond which the zone is silent. */
  radius: number;
  falloff: AudioZoneFalloff;
  category: AudioCategory;
}

/**
 * Gain for a listener `distance` from a zone centre: full volume at the
 * centre, silent at `radius`, shaped by the falloff curve. Pure.
 */
export function zoneAttenuation(distance: number, radius: number, falloff: AudioZoneFalloff): number {
  if (!(radius > 0)) return 0;
  const t = Math.min(1, Math.max(0, 1 - distance / radius));
  if (falloff === 'linear') return t;
  // Smoothstep eases both the centre and the edge.
  return t * t * (3 - 2 * t);
}

const FOOTSTEP_BUFFER_DURATION = 0.18;
export const MIN_GAIN = 0.0001;

function getAudioContextCtor(): typeof AudioContext | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
}

export function clampVolume(value: number) {
  return Math.min(1, Math.max(0, value));
}

export function createNoiseBuffer(context: AudioContext, durationSeconds: number) {
  const frameCount = Math.floor(context.sampleRate * durationSeconds);
  const buffer = context.createBuffer(1, frameCount, context.sampleRate);
  const channel = buffer.getChannelData(0);

  for (let index = 0; index < frameCount; index += 1) {
    channel[index] = Math.random() * 2 - 1;
  }

  return buffer;
}

function createFootstepBuffer(context: AudioContext) {
  const frameCount = Math.floor(context.sampleRate * FOOTSTEP_BUFFER_DURATION);
  const buffer = context.createBuffer(1, frameCount, context.sampleRate);
  const channel = buffer.getChannelData(0);

  for (let index = 0; index < frameCount; index += 1) {
    const envelope = 1 - index / frameCount;
    channel[index] = (Math.random() * 2 - 1) * envelope;
  }

  return buffer;
}

export function rampGain(gain: GainNode, value: number, time: number) {
  gain.gain.cancelScheduledValues(time);
  gain.gain.setValueAtTime(gain.gain.value, time);
  gain.gain.exponentialRampToValueAtTime(Math.max(MIN_GAIN, value), time + 1.1);
}

/**
 * Reusable Web Audio engine: builds the master/music/ambience/sfx graph and the
 * menu/game crossfade buses, applies the mix, and exposes generic UI + footstep
 * sounds. Games extend this and override `buildContentLayers` to populate the
 * buses with their own music/ambience, adding game-specific SFX methods.
 */
export class AudioEngine {
  protected context: AudioContext | null = null;
  protected compressor: DynamicsCompressorNode | null = null;
  protected masterGain: GainNode | null = null;
  protected musicGain: GainNode | null = null;
  protected ambienceGain: GainNode | null = null;
  protected sfxGain: GainNode | null = null;
  protected menuMusicBus: GainNode | null = null;
  protected menuAmbienceBus: GainNode | null = null;
  protected gameMusicBus: GainNode | null = null;
  protected gameAmbienceBus: GainNode | null = null;
  protected footstepBuffer: AudioBuffer | null = null;
  protected desiredMode: AudioMode = 'menu';
  protected settings: AudioMixSettings = {
    masterVolume: 0.9,
    musicVolume: 0.62,
    ambienceVolume: 0.78,
    sfxVolume: 0.86,
  };
  private lastHoverAt = 0;
  private bufferCache = new Map<string, Promise<AudioBuffer | null>>();
  private zoneVoices = new Map<string, { spec: AudioZoneVoiceSpec; source: AudioBufferSourceNode; gain: GainNode }>();
  private listener = { x: 0, z: 0 };
  private zoneGeneration = 0;

  ensureStarted() {
    const AudioContextCtor = getAudioContextCtor();
    if (!AudioContextCtor) {
      return;
    }

    if (!this.context) {
      this.context = new AudioContextCtor();
      this.initializeGraph(this.context);
      this.applySettings();
      this.applyMode();
    }

    if (this.context.state === 'suspended') {
      void this.context.resume();
    }
  }

  setMode(mode: AudioMode) {
    this.desiredMode = mode;
    this.applyMode();
  }

  setMix(settings: Partial<AudioMixSettings>) {
    this.settings = {
      ...this.settings,
      ...settings,
    };
    this.applySettings();
  }

  playUiHover() {
    this.ensureStarted();
    if (!this.context || !this.sfxGain) {
      return;
    }

    const now = performance.now();
    if (now - this.lastHoverAt < 60) {
      return;
    }
    this.lastHoverAt = now;

    const tone = this.context.createOscillator();
    tone.type = 'triangle';
    tone.frequency.setValueAtTime(680, this.context.currentTime);
    tone.frequency.exponentialRampToValueAtTime(920, this.context.currentTime + 0.05);

    const toneGain = this.context.createGain();
    toneGain.gain.setValueAtTime(MIN_GAIN, this.context.currentTime);
    toneGain.gain.exponentialRampToValueAtTime(0.018, this.context.currentTime + 0.01);
    toneGain.gain.exponentialRampToValueAtTime(MIN_GAIN, this.context.currentTime + 0.08);

    tone.connect(toneGain);
    toneGain.connect(this.sfxGain);
    tone.start(this.context.currentTime);
    tone.stop(this.context.currentTime + 0.09);
  }

  playUiConfirm() {
    this.ensureStarted();
    if (!this.context || !this.sfxGain) {
      return;
    }

    const now = this.context.currentTime;
    const tone = this.context.createOscillator();
    tone.type = 'sine';
    tone.frequency.setValueAtTime(280, now);
    tone.frequency.exponentialRampToValueAtTime(520, now + 0.14);

    const accent = this.context.createOscillator();
    accent.type = 'triangle';
    accent.frequency.setValueAtTime(420, now);
    accent.frequency.exponentialRampToValueAtTime(740, now + 0.11);

    const toneGain = this.context.createGain();
    toneGain.gain.setValueAtTime(MIN_GAIN, now);
    toneGain.gain.exponentialRampToValueAtTime(0.032, now + 0.02);
    toneGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.18);

    const accentGain = this.context.createGain();
    accentGain.gain.setValueAtTime(MIN_GAIN, now);
    accentGain.gain.exponentialRampToValueAtTime(0.018, now + 0.01);
    accentGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.13);

    tone.connect(toneGain);
    accent.connect(accentGain);
    toneGain.connect(this.sfxGain);
    accentGain.connect(this.sfxGain);

    tone.start(now);
    accent.start(now);
    tone.stop(now + 0.2);
    accent.stop(now + 0.16);
  }

  playFootstep(intensity = 1) {
    this.ensureStarted();
    if (!this.context || !this.sfxGain || !this.footstepBuffer || this.context.state !== 'running') {
      return;
    }

    const now = this.context.currentTime;

    const noise = this.context.createBufferSource();
    noise.buffer = this.footstepBuffer;

    const filter = this.context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(150 + Math.random() * 70, now);
    filter.Q.value = 0.8;

    const noiseGain = this.context.createGain();
    noiseGain.gain.setValueAtTime(MIN_GAIN, now);
    noiseGain.gain.exponentialRampToValueAtTime(Math.max(MIN_GAIN, 0.094 * intensity), now + 0.01);
    noiseGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.11);

    const thump = this.context.createOscillator();
    thump.type = 'triangle';
    thump.frequency.setValueAtTime(90, now);
    thump.frequency.exponentialRampToValueAtTime(46, now + 0.08);

    const thumpGain = this.context.createGain();
    thumpGain.gain.setValueAtTime(MIN_GAIN, now);
    thumpGain.gain.exponentialRampToValueAtTime(Math.max(MIN_GAIN, 0.036 * intensity), now + 0.01);
    thumpGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.08);

    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(this.sfxGain);

    thump.connect(thumpGain);
    thumpGain.connect(this.sfxGain);

    noise.start(now);
    noise.stop(now + 0.12);
    thump.start(now);
    thump.stop(now + 0.08);
  }

  private categoryGain(category: AudioCategory): GainNode | null {
    if (category === 'music') return this.musicGain;
    if (category === 'ambience') return this.ambienceGain;
    return this.sfxGain;
  }

  /**
   * Fetch + decode a source file once per URL, memoised on success. A
   * failure is NOT cached - a transient network blip or an audio file that
   * finishes uploading a moment after it was first requested would
   * otherwise be remembered as permanently broken (null, forever) for the
   * rest of the session, with no way to retry short of a full reload.
   */
  protected loadBuffer(url: string): Promise<AudioBuffer | null> {
    const cached = this.bufferCache.get(url);
    if (cached) return cached;
    const attempt = (async () => {
      if (!this.context) return null;
      try {
        const response = await fetch(url);
        if (!response.ok) return null;
        const bytes = await response.arrayBuffer();
        return await this.context.decodeAudioData(bytes);
      } catch {
        return null;
      }
    })().then((buffer) => {
      if (!buffer && this.bufferCache.get(url) === attempt) this.bufferCache.delete(url);
      return buffer;
    });
    this.bufferCache.set(url, attempt);
    return attempt;
  }

  /**
   * Replace the positional zone voices: each spec becomes a looping source
   * behind its own gain, attenuated by listener distance (updateListener).
   * Specs whose audio cannot be fetched or decoded are skipped.
   */
  async setZones(specs: AudioZoneVoiceSpec[]): Promise<void> {
    this.ensureStarted();
    this.stopZones();
    if (!this.context) return;
    const generation = ++this.zoneGeneration;
    await Promise.all(specs.map(async (spec) => {
      const buffer = await this.loadBuffer(spec.url);
      // A newer setZones/stopZones call wins over slow decodes.
      if (!buffer || !this.context || generation !== this.zoneGeneration) return;
      const target = this.categoryGain(spec.category);
      if (!target) return;
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const gain = this.context.createGain();
      const distance = Math.hypot(this.listener.x - spec.x, this.listener.z - spec.z);
      gain.gain.value = Math.max(MIN_GAIN, zoneAttenuation(distance, spec.radius, spec.falloff));
      source.connect(gain);
      gain.connect(target);
      source.start();
      this.zoneVoices.set(spec.id, { spec, source, gain });
    }));
  }

  /** Move the listener on the ground plane; zone gains follow smoothly. */
  updateListener(x: number, z: number): void {
    this.listener = { x, z };
    if (!this.context) return;
    const now = this.context.currentTime;
    for (const { spec, gain } of this.zoneVoices.values()) {
      const distance = Math.hypot(x - spec.x, z - spec.z);
      const value = Math.max(MIN_GAIN, zoneAttenuation(distance, spec.radius, spec.falloff));
      gain.gain.setTargetAtTime(value, now, 0.08);
    }
  }

  /** Stop and disconnect every zone voice. */
  stopZones(): void {
    this.zoneGeneration += 1;
    for (const { source, gain } of this.zoneVoices.values()) {
      try {
        source.stop();
      } catch {
        // Already stopped.
      }
      source.disconnect();
      gain.disconnect();
    }
    this.zoneVoices.clear();
  }

  /** Play a file once through a category bus (editor click-to-preview). */
  async playOneShot(url: string, category: AudioCategory = 'sfx'): Promise<boolean> {
    this.ensureStarted();
    if (!this.context) return false;
    const buffer = await this.loadBuffer(url);
    const target = this.categoryGain(category);
    if (!buffer || !this.context || !target) return false;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(target);
    source.start();
    return true;
  }

  /** Stop all zone voices and release the audio context. */
  dispose(): void {
    this.stopZones();
    if (this.context) {
      void this.context.close().catch(() => undefined);
      this.context = null;
    }
  }

  private initializeGraph(context: AudioContext) {
    this.compressor = context.createDynamicsCompressor();
    this.compressor.threshold.value = -18;
    this.compressor.knee.value = 14;
    this.compressor.ratio.value = 3;
    this.compressor.attack.value = 0.01;
    this.compressor.release.value = 0.25;

    this.masterGain = context.createGain();
    this.musicGain = context.createGain();
    this.ambienceGain = context.createGain();
    this.sfxGain = context.createGain();
    this.menuMusicBus = context.createGain();
    this.menuAmbienceBus = context.createGain();
    this.gameMusicBus = context.createGain();
    this.gameAmbienceBus = context.createGain();
    this.footstepBuffer = createFootstepBuffer(context);

    this.masterGain.gain.value = 0.9;
    this.musicGain.gain.value = 0.62;
    this.ambienceGain.gain.value = 0.78;
    this.sfxGain.gain.value = 0.86;

    this.menuMusicBus.gain.value = MIN_GAIN;
    this.menuAmbienceBus.gain.value = MIN_GAIN;
    this.gameMusicBus.gain.value = MIN_GAIN;
    this.gameAmbienceBus.gain.value = MIN_GAIN;

    this.menuMusicBus.connect(this.musicGain);
    this.gameMusicBus.connect(this.musicGain);
    this.menuAmbienceBus.connect(this.ambienceGain);
    this.gameAmbienceBus.connect(this.ambienceGain);

    this.musicGain.connect(this.masterGain);
    this.ambienceGain.connect(this.masterGain);
    this.sfxGain.connect(this.masterGain);
    this.masterGain.connect(this.compressor);
    this.compressor.connect(context.destination);

    this.buildContentLayers(context);
  }

  /**
   * Extension point: populate the menu/game music + ambience buses with content.
   * The base engine ships no content; a game overrides this to add its own.
   */
  protected buildContentLayers(_context: AudioContext): void {}

  private applySettings() {
    if (!this.context || !this.masterGain || !this.musicGain || !this.ambienceGain || !this.sfxGain) {
      return;
    }

    const now = this.context.currentTime;
    this.masterGain.gain.setTargetAtTime(clampVolume(this.settings.masterVolume), now, 0.12);
    this.musicGain.gain.setTargetAtTime(clampVolume(this.settings.musicVolume), now, 0.18);
    this.ambienceGain.gain.setTargetAtTime(clampVolume(this.settings.ambienceVolume), now, 0.18);
    this.sfxGain.gain.setTargetAtTime(clampVolume(this.settings.sfxVolume), now, 0.1);
  }

  private applyMode() {
    if (
      !this.context ||
      !this.menuMusicBus ||
      !this.menuAmbienceBus ||
      !this.gameMusicBus ||
      !this.gameAmbienceBus
    ) {
      return;
    }

    const now = this.context.currentTime;
    const isMenu = this.desiredMode === 'menu';

    rampGain(this.menuMusicBus, isMenu ? 1 : MIN_GAIN, now);
    rampGain(this.menuAmbienceBus, isMenu ? 0.52 : MIN_GAIN, now);
    rampGain(this.gameMusicBus, isMenu ? MIN_GAIN : 0.74, now);
    rampGain(this.gameAmbienceBus, isMenu ? MIN_GAIN : 1, now);
  }
}
